import React, { useEffect, useCallback, useState } from 'react';
import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { GoArrowLeft } from 'react-icons/go';
import { HiOutlineX } from 'react-icons/hi';

import { lockBodyScroll, unlockBodyScroll } from '../utils/bodyScrollLock';

import StickyFooter from './StickyFooter';

interface ResponsiveModalPageProps {
  isOpen: boolean;
  onClose: () => void;
  title: string; // center title text
  stickyFooter?: React.ReactNode;
  desktopWidthClass?: string;
  desktopHeightClass?: string;
  overlayClassName?: string;
  containerClassName?: string;
  closeOnOverlay?: boolean;
  zIndexClass?: string;
  onBack?: () => void; // optional custom back (defaults to onClose)
}

// A reusable layout full-screen on mobile, centered modal on md+. Includes built-in header.
const ResponsiveModalPage: React.FC<
  PropsWithChildren<ResponsiveModalPageProps>
> = ({
  isOpen,
  onClose,
  title,
  children,
  stickyFooter,
  desktopWidthClass = 'md:w-1/2',
  desktopHeightClass = 'md:h-[80vh]',
  overlayClassName = '',
  containerClassName = '',
  closeOnOverlay = true,
  zIndexClass = 'z-50',
  onBack,
}) => {
  const { t } = useTranslation();
  // animation mount/unmount state (mirror ResponsiveModalBottomSheet behaviour)
  const [isVisible, setIsVisible] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);

  // Manage mount/unmount for animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      const timer = setTimeout(() => setShouldAnimate(true), 10);
      return () => clearTimeout(timer);
    }
    setShouldAnimate(false);
    const timer = setTimeout(() => setIsVisible(false), 300);
    return () => clearTimeout(timer);
  }, [isOpen]);

  // Prevent background scroll (iOS-safe and supports nested modals)
  useEffect(() => {
    if (!isVisible) return;
    lockBodyScroll();
    return () => unlockBodyScroll();
  }, [isVisible]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onKeyDown]);

  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 ${zIndexClass}`}
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      {/* Overlay with fade animation */}
      <div
        className={`absolute inset-0 bg-black transition-opacity duration-300 ${shouldAnimate ? 'opacity-50' : 'opacity-0'} ${overlayClassName}`}
        onClick={() => closeOnOverlay && onClose()}
        aria-hidden="true"
      />

      {/* Desktop centered modal (md+) with scale/fade animation */}
      <div
        className={`md:shadow-card hidden bg-white md:absolute md:top-1/2 md:left-1/2 md:flex md:-translate-x-1/2 md:-translate-y-1/2 md:flex-col md:rounded-2xl ${desktopWidthClass} ${desktopHeightClass} ${containerClassName} ${shouldAnimate ? 'md:scale-100 md:opacity-100' : 'md:scale-95 md:opacity-0'} transition-all duration-300`}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 pt-4 pb-2">
          <button
            onClick={onBack || onClose}
            className="flex h-9 items-center gap-1.5 md:hidden"
            aria-label={t('common.back')}
            type="button"
          >
            <GoArrowLeft className="h-5 w-5" />
            <span className="text-primary-text leading-6">
              {t('common.back')}
            </span>
          </button>
          <h1 className="text-primary-text mx-auto text-xl leading-7 font-semibold md:mx-0 md:text-lg md:leading-[30px]">
            {title}
          </h1>
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="hidden h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-gray-100 md:flex"
            type="button"
          >
            <HiOutlineX className="h-6 w-6 text-gray-500" />
          </button>
        </div>
        <div
          data-scroll-lock-target
          className="flex min-h-0 flex-1 flex-col overflow-y-auto"
        >
          {children}
        </div>
        {stickyFooter && <StickyFooter>{stickyFooter}</StickyFooter>}
      </div>

      {/* Mobile full-screen sliding panel (slides up from bottom) */}
      <div
        className={`absolute inset-0 flex transform flex-col bg-white transition-transform duration-300 ease-out md:hidden ${shouldAnimate ? 'translate-y-0' : 'translate-y-full'} ${containerClassName}`}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between px-4 pt-4 pb-2">
          <button
            onClick={onBack || onClose}
            className="flex h-9 items-center gap-1.5 md:hidden"
            aria-label={t('common.back')}
            type="button"
          >
            <GoArrowLeft className="h-5 w-5" />
            <span className="text-primary-text leading-6">
              {t('common.back')}
            </span>
          </button>
          <button
            onClick={onClose}
            aria-label={t('common.close')}
            className="hidden h-9 w-9 cursor-pointer items-center justify-center rounded-full transition-colors hover:bg-gray-100 md:flex"
            type="button"
          >
            <HiOutlineX className="h-6 w-6 text-gray-500" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <h1 className="text-primary-text mt-2 px-4 text-xl leading-7 font-semibold md:mx-0 md:text-[22px] md:leading-[30px]">
            {title}
          </h1>
          <div
            data-scroll-lock-target
            className="flex min-h-0 flex-1 flex-col overflow-y-auto"
          >
            {children}
          </div>
        </div>
        {stickyFooter && <StickyFooter>{stickyFooter}</StickyFooter>}
      </div>
    </div>
  );
};

export default ResponsiveModalPage;
